import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { createPageUrl } from '../utils';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import {
  MapPin,
  Monitor,
  Mic2,
  Palette,
  Info,
  FileText,
  LayoutDashboard,
  Menu,
  X,
  ChevronDown,
  LogOut,
  Settings,
  Search,
  Pencil,
  Shield,
  User,
  BookOpen
} from 'lucide-react';
import SearchModal from '@/components/portal/SearchModal';
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

const navSections = [
  { name: 'Venue', icon: MapPin, page: 'EventPortal', section: 'venue' },
  { name: 'Speakers', icon: Mic2, page: 'EventPortal', section: 'speakers' },
  { name: 'Branding', icon: Palette, page: 'EventPortal', section: 'branding' },
  { name: 'On-Site Info', icon: Info, page: 'EventPortal', section: 'onsite' },
  { name: 'Change Log', icon: FileText, page: 'EventPortal', section: 'changelog' },
  { name: 'Resources', icon: BookOpen, page: 'Resources', isPage: true },
  { name: 'Access & Sharing', icon: Settings, page: 'EventPortal', section: 'access', adminOnly: true },
];

interface LayoutProps {
  children: React.ReactNode;
  currentPageName: string;
}

export default function Layout({ children, currentPageName }: LayoutProps) {
  const { user, profile, signOut } = useAuth();
  const [searchParams] = useSearchParams();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentEvent, setCurrentEvent] = useState<any>(null);
  const [searchOpen, setSearchOpen] = useState(false);

  const eventId = searchParams.get('eventId');

  useEffect(() => {
    console.log('[Layout] Profile state updated:', profile);
    console.log('[Layout] Is admin?', profile?.role === 'admin');
    console.log('[Layout] Profile role:', profile?.role);
  }, [profile]);

  useEffect(() => {
    const loadCurrentEvent = async () => {
      if (eventId) {
        try {
          const { data } = await supabase
            .from('events')
            .select('*')
            .eq('id', eventId)
            .maybeSingle();

          if (data) {
            setCurrentEvent(data);
          }
        } catch (e) {
          console.error('Failed to load event');
        }
      }
    };

    loadCurrentEvent();
  }, [eventId]);

  const handleLogout = async () => {
    await signOut();
  };

  const activeSection = searchParams.get('section') || 'overview';

  if (currentPageName === 'Home' || currentPageName === 'Events' || currentPageName === 'Settings' || (!eventId && currentPageName !== 'Resources')) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <style>{`
          :root {
            --background: 0 0% 4%;
            --foreground: 0 0% 98%;
            --card: 0 0% 10%;
            --card-foreground: 0 0% 98%;
            --primary: 22 95% 54%;
            --primary-foreground: 0 0% 98%;
            --secondary: 0 0% 12%;
            --secondary-foreground: 0 0% 98%;
            --muted: 0 0% 12%;
            --muted-foreground: 0 0% 64%;
            --accent: 22 95% 54%;
            --accent-foreground: 0 0% 98%;
            --border: 0 0% 12%;
            --input: 0 0% 12%;
            --ring: 22 95% 54%;
          }
        `}</style>

        <header className="border-b border-zinc-800/50 bg-[#0a0a0a]/80 backdrop-blur-xl sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to={createPageUrl('Home')} className="flex items-center gap-3">
                <img
                  src="/slidepusher_events.svg"
                  alt="Slidepusher Events"
                  className="h-20"
                />
              </Link>

              <div className="flex items-center gap-4">
                {user ? (
                  <>
                    {console.log('[LANDING PAGE RENDER] user:', user?.email, 'profile:', profile, 'role:', profile?.role)}
                    <DropdownMenu key={`dropdown-${profile?.role || 'no-role'}`}>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="text-zinc-300 hover:text-white hover:bg-zinc-800">
                          {profile?.avatar_url ? (
                            <img
                              src={profile.avatar_url}
                              alt="User avatar"
                              className="w-7 h-7 rounded-full object-cover mr-2"
                            />
                          ) : (
                            <div className="w-7 h-7 bg-zinc-700 rounded-full flex items-center justify-center mr-2">
                              <span className="text-xs font-medium text-white">
                                {user.user_metadata?.full_name?.charAt(0) || user.email?.charAt(0) || 'U'}
                              </span>
                            </div>
                          )}
                          <span className="hidden sm:inline">{user.user_metadata?.full_name || user.email}</span>
                          <ChevronDown className="w-4 h-4 ml-2" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48 bg-zinc-900 border-zinc-800">
                        {console.log('[LANDING DROPDOWN OPENED] Profile:', profile, 'Role:', profile?.role, 'IsAdmin:', profile?.role === 'admin')}
                        {(() => {
                        console.log('[Layout] Dropdown render - profile:', profile);
                        console.log('[Layout] Dropdown render - profile?.role:', profile?.role);
                        console.log('[Layout] Dropdown render - is admin?:', profile?.role === 'admin');
                        return profile?.role === 'admin';
                      })() && (
                        <>
                          <DropdownMenuItem asChild className="text-zinc-300 focus:text-white focus:bg-zinc-800 cursor-pointer">
                            <Link to={eventId ? `${createPageUrl('AdminUsers')}?eventId=${eventId}` : createPageUrl('AdminUsers')}>
                              <Shield className="w-4 h-4 mr-2" />
                              Manage Users
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-zinc-800" />
                        </>
                      )}
                      <DropdownMenuItem asChild className="text-zinc-300 focus:text-white focus:bg-zinc-800 cursor-pointer">
                        <Link to={eventId ? `${createPageUrl('Settings')}?eventId=${eventId}` : createPageUrl('Settings')}>
                          <Settings className="w-4 h-4 mr-2" />
                          Settings
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-zinc-800" />
                      <DropdownMenuItem onClick={handleLogout} className="text-zinc-300 focus:text-white focus:bg-zinc-800">
                        <LogOut className="w-4 h-4 mr-2" />
                        Sign Out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  </>
                ) : (
                  <Link to={createPageUrl('Login')}>
                    <Button className="bg-primary-500 text-white hover:bg-primary-600 border-primary-500">
                      Sign In
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </header>

        <main>{children}</main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex">
      <style>{`
        :root {
          --background: 0 0% 4%;
          --foreground: 0 0% 98%;
          --card: 0 0% 10%;
          --card-foreground: 0 0% 98%;
          --primary: 22 95% 54%;
          --primary-foreground: 0 0% 98%;
          --secondary: 0 0% 12%;
          --secondary-foreground: 0 0% 98%;
          --muted: 0 0% 12%;
          --muted-foreground: 0 0% 64%;
          --accent: 22 95% 54%;
          --accent-foreground: 0 0% 98%;
          --border: 0 0% 12%;
          --input: 0 0% 12%;
          --ring: 22 95% 54%;
        }

      `}</style>

      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden pointer-events-auto"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside className={cn(
        "fixed top-0 left-0 z-50 w-64 h-screen bg-[#1a1a1a]/95 backdrop-blur-xl border-r border-zinc-800/50 flex flex-col transition-transform duration-300 ease-out pointer-events-auto",
        sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
      )}>
        <div className="p-4 border-b border-zinc-800/50">
          <div className="flex items-center justify-between">
            <Link to={createPageUrl('Home')} className="flex items-center gap-3">
              <img
                src="/slidepusher_events.svg"
                alt="Slidepusher Events"
                className="h-20"
              />
            </Link>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden text-zinc-400 hover:text-white"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {currentEvent && (
          <div className="p-4 border-b border-zinc-800/50">
            <div className="flex items-center gap-3">
              {currentEvent.avatar_url ? (
                <img
                  src={currentEvent.avatar_url}
                  alt={currentEvent.name}
                  className="w-10 h-10 rounded-lg object-cover border border-zinc-700"
                />
              ) : (
                <div className="w-10 h-10 rounded-lg bg-zinc-700 flex items-center justify-center">
                  <span className="text-xs font-semibold text-white">
                    {currentEvent.name?.substring(0, 2).toUpperCase()}
                  </span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-0.5">Current Event</p>
                <p className="text-sm font-medium text-white truncate">{currentEvent.name}</p>
              </div>
            </div>
          </div>
        )}

        <nav
          id="sidebar-nav"
          className="flex-1 overflow-y-auto py-4 px-3"
        >
          <div className="space-y-1">
            {navSections.map((item) => {
              const isActive = item.isPage
                ? currentPageName === item.page
                : activeSection === item.section;
              if (item.adminOnly && profile?.role !== 'admin') return null;

              const linkUrl = item.isPage
                ? `${createPageUrl(item.page)}?eventId=${eventId}`
                : createPageUrl(item.page) + `?eventId=${eventId}&section=${item.section}`;

              return (
                <Link
                  key={item.section || item.name}
                  to={linkUrl}
                  onClick={() => setSidebarOpen(false)}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded text-sm font-medium transition-all duration-200",
                    isActive
                      ? "bg-primary-500 text-white"
                      : "text-zinc-400 hover:text-white hover:bg-zinc-800/50"
                  )}
                >
                  <item.icon className="w-4 h-4 flex-shrink-0" />
                  {item.name}
                </Link>
              );
            })}
          </div>
        </nav>

        <div className="p-4 border-t border-zinc-800/50">
          <Link
            to={createPageUrl('Events')}
            onClick={() => setSidebarOpen(false)}
            className="flex items-center gap-2 text-sm text-zinc-500 hover:text-white transition-colors px-3 py-2"
          >
            <ChevronDown className="w-4 h-4 rotate-90" />
            All Events
          </Link>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-h-screen lg:ml-64">
        <header className="fixed top-0 left-0 right-0 lg:left-64 z-[60] bg-[#0a0a0a]/80 backdrop-blur-xl border-b border-zinc-800/50 pointer-events-auto">
          <div className="flex items-center justify-between h-14 px-4 lg:px-6">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden text-zinc-400 hover:text-white"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-5 h-5" />
              </Button>
              <h1 className="text-lg font-semibold text-white">
                {currentPageName === 'Resources' ? 'Resources' : activeSection === 'overview' ? 'Overview' : navSections.find(s => s.section === activeSection)?.name || 'Event Portal'}
              </h1>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSearchOpen(true)}
                className="text-zinc-400 hover:text-white hover:bg-zinc-800 hidden sm:flex"
              >
                <Search className="w-4 h-4 mr-2" />
                <span className="text-sm">Search</span>
                <kbd className="ml-2 pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border border-zinc-700 bg-zinc-800 px-1.5 font-mono text-[10px] font-medium text-zinc-400">
                  ⌘K
                </kbd>
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSearchOpen(true)}
                className="text-zinc-400 hover:text-white hover:bg-zinc-800 sm:hidden h-8 w-8"
              >
                <Search className="w-4 h-4" />
              </Button>
              <Link to={createPageUrl('EditEvent') + `?eventId=${eventId}`}>
                <Button variant="ghost" size="icon" className="text-zinc-400 hover:text-white hover:bg-zinc-800 h-8 w-8">
                  <Pencil className="w-4 h-4" />
                </Button>
              </Link>
              {currentEvent?.google_drive_url && (
                <a
                  href={currentEvent.google_drive_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Open Google Drive Folder"
                  className="flex-shrink-0"
                >
                  <Button variant="ghost" size="icon" className="text-zinc-400 hover:text-white hover:bg-zinc-800 h-8 w-8">
                    <img src="/google-drive.png" alt="Google Drive" className="w-4 h-4" />
                  </Button>
                </a>
              )}
              {user && (
                <>
                  <DropdownMenu key={`mobile-dropdown-${profile?.role || 'no-role'}`}>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-zinc-300 hover:text-white hover:bg-zinc-800">
                        {profile?.avatar_url ? (
                          <img
                            src={profile.avatar_url}
                            alt="User avatar"
                            className="w-6 h-6 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-6 h-6 bg-zinc-700 rounded-full flex items-center justify-center">
                            <span className="text-xs font-medium text-white">
                              {user.user_metadata?.full_name?.charAt(0) || user.email?.charAt(0) || 'U'}
                            </span>
                          </div>
                        )}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48 bg-zinc-900 border-zinc-800">
                      {console.log('[EVENT PORTAL DROPDOWN OPENED] Profile:', profile, 'Role:', profile?.role, 'IsAdmin:', profile?.role === 'admin')}
                      {(() => {
                        console.log('[Layout] EventPortal dropdown render - profile:', profile);
                        console.log('[Layout] EventPortal dropdown render - profile?.role:', profile?.role);
                        console.log('[Layout] EventPortal dropdown render - is admin?:', profile?.role === 'admin');
                        return profile?.role === 'admin';
                      })() && (
                      <>
                        <DropdownMenuItem asChild className="text-zinc-300 focus:text-white focus:bg-zinc-800 cursor-pointer">
                          <Link to={`${createPageUrl('AdminUsers')}?eventId=${eventId}`}>
                            <Shield className="w-4 h-4 mr-2" />
                            Manage Users
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator className="bg-zinc-800" />
                      </>
                    )}
                    <DropdownMenuItem asChild className="text-zinc-300 focus:text-white focus:bg-zinc-800 cursor-pointer">
                      <Link to={`${createPageUrl('Settings')}?eventId=${eventId}`}>
                        <Settings className="w-4 h-4 mr-2" />
                        Settings
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-zinc-800" />
                    <DropdownMenuItem onClick={handleLogout} className="text-zinc-300 focus:text-white focus:bg-zinc-800">
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                </>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-auto pt-14">
          {children}
        </main>
      </div>

      <SearchModal
        eventId={eventId}
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
      />
    </div>
  );
}
